import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Shield,
  Store,
  Users,
  FileCheck,
  TrendingUp,
  ArrowRight,
  Activity,
  AlertCircle,
  ClipboardList,
  CheckCircle,
  XCircle,
  MessageSquare,
  Clock,
  FileText,
  Info,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from '../../lib/safeToast';

function KpiCard({ icon: Icon, title, value, accent, onClick }) {
  return (
    <Card
      className={`border-[#DCE4E0] shadow-sm transition-all ${onClick ? 'cursor-pointer hover:border-[#0B6445]/50 hover:shadow-md group' : ''}`}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
          <Icon className={`w-4 h-4 ${accent || ''}`} />
          {title}
          {onClick && <Info className="w-3 h-3 ml-auto text-slate-300 group-hover:text-[#0B6445] transition-colors" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-bold text-[#073B2E]">{value ?? '—'}</p>
      </CardContent>
    </Card>
  );
}

function EnvBadge({ isLive }) {
  return (
    <span
      className={isLive
        ? 'inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-[#E6F7F3] px-3 py-1 text-sm font-medium text-[#0B6445]'
        : 'inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-800'}
    >
      <span className={`h-2 w-2 rounded-full ${isLive ? 'bg-emerald-500' : 'bg-amber-500'}`} aria-hidden />
      Viewing: {isLive ? 'Live' : 'Sandbox'}
    </span>
  );
}

function RecentActivityCard({ activity, formatDate }) {
  return (
    <Card className="border-[#DCE4E0] shadow-sm">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4" /> Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activity.length > 0 ? (
          <ul className="space-y-2 max-h-[280px] overflow-y-auto">
            {activity.map((log, i) => (
              <li key={log.id || i} className="flex flex-col gap-0.5 py-2 border-b border-[#DCE4E0] last:border-0">
                <span className="text-sm font-medium text-[#073B2E]">{log.action}</span>
                <span className="text-xs text-[#0E1B16]/60">{formatDate(log.timestamp)}</span>
                {log.details && Object.keys(log.details).length > 0 && (
                  <span className="text-xs text-[#0E1B16]/70 truncate">
                    {typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[#0E1B16]/60 py-4">No recent activity.</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ── CO-specific Dashboard ── */
function CODashboard({ api, user, isLive }) {
  const navigate = useNavigate();
  const goPerf = () => navigate('/backoffice/compliance/performance');
  const [coStats, setCoStats] = useState(null);
  const [queueCount, setQueueCount] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          api.get('/backoffice/kyc/co-performance'),
          api.get('/backoffice/kyc/my-queue'),
          api.get('/backoffice/recent-activity?limit=15'),
        ]);
        if (results[0].status === 'fulfilled') setCoStats(results[0].value.data?.data || null);
        if (results[1].status === 'fulfilled') setQueueCount(results[1].value.data?.meta?.total || results[1].value.data?.data?.length || 0);
        if (results[2].status === 'fulfilled') setRecentActivity(results[2].value.data?.data || []);
      } catch {
        toast.warning('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, isLive]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="spinner w-8 h-8" />
        <p className="text-sm text-slate-500">Loading dashboard…</p>
      </div>
    );
  }

  const s = coStats || {};

  return (
    <div className="space-y-8" data-testid="backoffice-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-[#0B6445]" />
            Compliance Dashboard
          </h1>
          <p className="text-[#0E1B16]/70 mt-2">
            Overview for {user?.name || user?.email}. Your KYC review activity and queue.
          </p>
        </div>
        <EnvBadge isLive={isLive} />
      </div>

      {/* Queue + Key KPIs */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-[#0B6445]/30 shadow-sm bg-[#E6F7F3]/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#0B6445] flex items-center gap-2">
              <FileCheck className="w-4 h-4" /> My Queue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#073B2E]">{queueCount}</p>
            {queueCount > 0 && (
              <Link to="/backoffice/compliance/my-queue" className="text-sm text-[#0B6445] hover:underline mt-1 inline-block">
                View queue →
              </Link>
            )}
          </CardContent>
        </Card>
        <KpiCard icon={Store} title="Merchants Reviewed" value={s.total_reviewed ?? 0} onClick={goPerf} />
        <KpiCard icon={FileText} title="Total Actions" value={s.total_actions ?? 0} onClick={goPerf} />
        <KpiCard icon={TrendingUp} title="Approval Rate" value={s.approval_rate != null ? `${s.approval_rate}%` : '0%'} accent="text-[#0B6445]" onClick={goPerf} />
      </div>

      {/* Detailed KPIs */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard icon={CheckCircle} title="Docs Accepted" value={s.docs_accepted ?? 0} accent="text-emerald-600" onClick={goPerf} />
        <KpiCard icon={MessageSquare} title="Updates Requested" value={s.docs_update_requested ?? 0} accent="text-amber-600" onClick={goPerf} />
        <KpiCard icon={Clock} title="Avg Review Time" value={s.avg_review_time_hours != null ? `${s.avg_review_time_hours}h` : '—'} accent="text-blue-600" onClick={goPerf} />
      </div>

      {/* Formal Recommendations */}
      <div className="grid gap-6 sm:grid-cols-3">
        <KpiCard icon={CheckCircle} title="Recommended Approve" value={s.approved ?? 0} accent="text-emerald-600" onClick={goPerf} />
        <KpiCard icon={XCircle} title="Recommended Reject" value={s.rejected ?? 0} accent="text-red-600" onClick={goPerf} />
        <KpiCard icon={MessageSquare} title="Info Requested" value={s.info_requested ?? 0} accent="text-amber-600" onClick={goPerf} />
      </div>

      {/* Recent Activity */}
      <RecentActivityCard activity={recentActivity} formatDate={formatDate} />
    </div>
  );
}

/* ── SU / Operations Dashboard ── */
function SUDashboard({ api, user, isLive, isSU }) {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const TIMEOUT_MS = 8000;
    const MAX_LOADING_MS = 10000;
    const fetchData = async () => {
      setLoading(true);
      const forceDoneTimer = setTimeout(() => setLoading(false), MAX_LOADING_MS);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
        const opts = { signal: controller.signal, timeout: TIMEOUT_MS };
        const results = await Promise.allSettled([
          api.get('/backoffice/dashboard-stats', opts),
          api.get('/backoffice/new-registrations-7d', opts),
          api.get('/backoffice/recent-activity?limit=20', opts),
        ]);
        clearTimeout(timeoutId);
        clearTimeout(forceDoneTimer);
        const [statsRes, chartRes, activityRes] = results;
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        if (chartRes.status === 'fulfilled') setChartData(chartRes.value.data?.data || []);
        if (activityRes.status === 'fulfilled') setRecentActivity(activityRes.value.data?.data || []);
        if (results.every(r => r.status === 'rejected')) {
          toast.warning('Dashboard data could not be loaded.');
        }
      } catch {
        clearTimeout(forceDoneTimer);
        toast.warning('Dashboard data could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, isLive]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  if (loading && !stats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="spinner w-8 h-8" />
        <p className="text-sm text-slate-500">Loading dashboard…</p>
      </div>
    );
  }

  const s = stats || {};
  const pendingItems = [
    ...(s.pending_kyc > 0 ? [{ label: 'KYC applications awaiting review', count: s.pending_kyc, to: '/backoffice/kyc/pending' }] : []),
  ].slice(0, 5);

  return (
    <div className="space-y-8" data-testid="backoffice-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#0B6445]" />
            {isSU ? 'Super Admin Dashboard' : 'Operations Dashboard'}
          </h1>
          <p className="text-[#0E1B16]/70 mt-2">
            Overview for {user?.name || user?.email}. {isSU ? 'Platform KPIs and recent activity.' : 'Merchant management, KYC approvals and platform activity.'}
          </p>
        </div>
        <EnvBadge isLive={isLive} />
      </div>

      {/* KPI Cards */}
      <div className="flex flex-col gap-6">
        {/* Row 1 */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard icon={Store} title="Total Merchants" value={s.total_merchants ?? 0} />
          {isSU ? (
            <Card className="border-[#DCE4E0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                  <FileCheck className="w-4 h-4" /> Pending KYC Queue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-[#073B2E]">{s.pending_kyc ?? 0}</p>
                {s.pending_kyc > 0 && (
                  <Link to="/backoffice/kyc/pending" className="text-sm text-[#0B6445] hover:underline mt-1 inline-block">
                    View queue →
                  </Link>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-[#DCE4E0] shadow-sm border-l-4 border-l-[#0B6445]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" /> Pending Final Approval
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-[#073B2E]">{s.kyc_pending_final ?? 0}</p>
                {(s.kyc_pending_final ?? 0) > 0 && (
                  <Link to="/backoffice/kyc/pending" className="text-sm text-[#0B6445] hover:underline mt-1 inline-block">
                    Review & approve →
                  </Link>
                )}
              </CardContent>
            </Card>
          )}
          <KpiCard icon={Users} title="Total Users" value={s.total_users ?? 0} />
          <KpiCard icon={TrendingUp} title="New Registrations (7d)" value={s.new_registrations_7d ?? 0} />
        </div>

        {/* Row 2 — Operations only */}
        {!isSU && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" /> KYC In Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-700">{s.kyc_in_review ?? 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">Being reviewed by CO</p>
            </CardContent>
          </Card>
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-emerald-500" /> CO Approved
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-emerald-700">{s.kyc_co_approved ?? 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">Awaiting your final approval</p>
              {(s.kyc_co_approved ?? 0) > 0 && (
                <Link to="/backoffice/kyc/pending" className="text-sm text-[#0B6445] hover:underline mt-1 inline-block">
                  Review now →
                </Link>
              )}
            </CardContent>
          </Card>
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" /> Info Requested
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-700">{s.kyc_info_requested ?? 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">Waiting for merchant response</p>
            </CardContent>
          </Card>
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" /> KYC Approved / Rejected
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-3">
                <span className="text-2xl font-bold text-green-700">{s.kyc_approved ?? 0}</span>
                <span className="text-sm text-slate-400">/</span>
                <span className="text-2xl font-bold text-red-600">{s.kyc_rejected ?? 0}</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Approved / Rejected total</p>
            </CardContent>
          </Card>
        </div>
      )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">New Registrations (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#0B6445" fill="#E6F7F3" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-[#0E1B16]/60 py-8 text-center">No registration data for the last 7 days.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Approval Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-[#073B2E]">{s.approval_rate ?? 0}%</p>
            <p className="text-sm text-[#0E1B16]/60 mt-1">KYC applications approved (of those with a status).</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Top Pending Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingItems.length > 0 ? (
              <ul className="space-y-2">
                {pendingItems.map((item, i) => (
                  <li key={i}>
                    <Link to={item.to} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#E6F7F3] text-[#073B2E]">
                      <span>{item.label}</span>
                      <span className="text-sm font-semibold">{item.count}</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#0E1B16]/60 py-4">No pending actions requiring attention.</p>
            )}
          </CardContent>
        </Card>

        <RecentActivityCard activity={recentActivity} formatDate={formatDate} />
      </div>
    </div>
  );
}

const BackOfficeDashboard = () => {
  const { api, user } = useAuth();
  const { isLive } = useEnvironment();

  const isSU = user?.role === 'admin' || user?.role === 'super_admin';
  const isCO = user?.role === 'compliance_officer' || (user?.permissions || []).includes('compliance_officer');

  return (
    <BackOfficeLayout>
      {isCO && !isSU ? (
        <CODashboard api={api} user={user} isLive={isLive} />
      ) : (
        <SUDashboard api={api} user={user} isLive={isLive} isSU={isSU} />
      )}
    </BackOfficeLayout>
  );
};

export default BackOfficeDashboard;
